import apiClient, { apiService } from './api';

export interface BarcodePrintSettings {
  columns: number;
  rows: number;
  pageMarginMm: number;
  horizontalGapMm: number;
  verticalGapMm: number;
  barcodeHeightMm: number;
  showCompanyName: boolean;
  showProductName: boolean;
  showSku: boolean;
  showPrice: boolean;
  fontSizePt: number;
}

export interface BulkBarcodeLabelItem {
  productId: number;
  quantity: number;
}

/**
 * With responseType 'blob', axios puts the error body in a Blob even when the
 * server sent JSON — parse it back so the real backend message (not a
 * generic fallback) reaches the user.
 */
async function recoverJsonBlobError(err: unknown): Promise<never> {
  const axiosErr = err as { response?: { data?: unknown } };
  const data = axiosErr?.response?.data;
  if (data instanceof Blob && data.type.includes('json')) {
    try {
      axiosErr.response!.data = JSON.parse(await data.text());
    } catch {
      /* leave as-is if it isn't valid JSON */
    }
  }
  throw err;
}

/**
 * Thrown when the PDF was generated successfully but the browser tab it was
 * meant to open in couldn't display it (e.g. a pop-up blocker, or the
 * pre-opened placeholder tab having been closed/navigated away by the user
 * or an extension in the time it took the request to complete). Callers
 * should show a distinct message for this — it is not a generation failure.
 */
export class PdfDisplayError extends Error {
  constructor() {
    super('Unable to open the generated PDF — it may have been blocked by a pop-up blocker.');
    this.name = 'PdfDisplayError';
  }
}

function openPdfBlob(blob: Blob, targetTab?: Window | null) {
  const url = URL.createObjectURL(blob);
  try {
    if (targetTab && !targetTab.closed) {
      targetTab.location.href = url;
      return;
    }
  } catch {
    // targetTab navigated to a different origin (blocked/redirected) between
    // being opened and the PDF arriving — fall through to a fresh tab below.
  }
  if (!window.open(url, '_blank')) {
    throw new PdfDisplayError();
  }
}

export const barcodeService = {
  getSettings: () =>
    apiService.get<BarcodePrintSettings>('/barcode-settings'),

  updateSettings: (data: BarcodePrintSettings) =>
    apiService.put<BarcodePrintSettings>('/barcode-settings', data),

  /**
   * Fetches the label sheet PDF and shows it in a browser tab (the built-in
   * viewer provides zoom + print). Pass a pre-opened tab to survive popup
   * blockers when awaiting the request.
   */
  openLabelsPdf: async (productId: number, quantity: number, targetTab?: Window | null) => {
    try {
      const res = await apiClient.get<Blob>(`/products/${productId}/barcodes/pdf`, {
        params: { quantity },
        responseType: 'blob',
      });
      openPdfBlob(res.data, targetTab);
    } catch (err) {
      await recoverJsonBlobError(err);
    }
  },

  /**
   * Fetches one combined label sheet covering several products at once (e.g.
   * a batch of selected sales items) and shows it in a browser tab.
   */
  openBulkLabelsPdf: async (items: BulkBarcodeLabelItem[], targetTab?: Window | null) => {
    try {
      const res = await apiClient.post<Blob>(
        '/products/barcodes/pdf/bulk',
        { items },
        { responseType: 'blob' }
      );
      openPdfBlob(res.data, targetTab);
    } catch (err) {
      await recoverJsonBlobError(err);
    }
  },
};

export default barcodeService;
